import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import api, { fetchCourses, fetchDepartments } from '../services/api';
import { useInstitution } from './InstitutionContext';
import { useAuth } from './AuthContext';
import usePrevSubmissionDisplay from '../utils/usePrevSubmissionDisplay';
import IconOnlyChip from './IconOnlyChip';

const EditProfessorCourseAdminDialog = ({ open, onClose, link: linkProp, onResubmit }) => {
  const link = linkProp ?? {};
  const { selectedInstitution } = useInstitution();
  const { user } = useAuth();
  const prevDisplay = usePrevSubmissionDisplay(link.previous_submission);
  const [courses, setCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptId, setDeptId] = useState('');
  const [editCourse, setEditCourse] = useState(false);
  const [editProf, setEditProf] = useState(false);
  const [courseId, setCourseId] = useState('');
  const [profName, setProfName] = useState('');
  const [courseDirty, setCourseDirty] = useState(false);
  const [profDirty, setProfDirty] = useState(false);
  const [allProfNames, setAllProfNames] = useState([]);
  const [profDuplicate, setProfDuplicate] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const toggleEditCourse = () => {
    setEditCourse((prev) => {
      if (prev) {
        setCourseId(link.course?.id || '');
        setDeptId(link.course?.department?.id || '');
        setCourseDirty(false);
      }
      return !prev;
    });
  };

  const toggleEditProf = () => {
    setEditProf((prev) => {
      if (prev) {
        setProfName('');
        setProfDuplicate(false);
        setProfDirty(false);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (!open) return;
    setEditCourse(false);
    setEditProf(false);
    setCourseId(link.course?.id || '');
    setProfName('');
    setCourseDirty(false);
    setProfDirty(false);
    setProfDuplicate(false);
    setConfirmed(false);
    const instId = link.course?.department?.institution?.id || selectedInstitution;
    setDeptId(link.course?.department?.id || '');
    if (instId) {
      fetchDepartments(instId)
        .then((res) => setDepartments(res || []))
        .catch(() => setDepartments([]));
      fetchCourses(instId)
        .then((res) => {
          const list = res.results || res;
          setAllCourses(list || []);
          const currentDept = link.course?.department?.id;
          setCourses(
            list.filter((c) => c.department?.id === currentDept)
          );
        })
        .catch(() => {
          setAllCourses([]);
          setCourses([]);
        });
      const nameInst =
        user?.userprofile?.institution?.id ||
        selectedInstitution ||
        link.course?.department?.institution?.id;
      api
        .get('/courses/professor-names/', {
          params: { institution_id: nameInst },
        })
        .then((res) => setAllProfNames(res.data || []))
        .catch(() => setAllProfNames([]));
    } else {
      setDepartments([]);
      setAllCourses([]);
      setCourses([]);
      setAllProfNames([]);
    }
  }, [open, link, user, selectedInstitution]);

  useEffect(() => {
    if (!deptId) {
      setCourses(allCourses);
    } else {
      setCourses(allCourses.filter((c) => c.department?.id === deptId));
    }
  }, [deptId, allCourses]);

  useEffect(() => {
    if (!editProf) {
      setProfDuplicate(false);
      return;
    }
    const dup = allProfNames.some(
      (n) => n.trim().toLowerCase() === profName.trim().toLowerCase()
    );
    setProfDuplicate(dup);
  }, [profName, allProfNames, editProf]);

  const changed =
    (editCourse && courseDirty && courseId && courseId !== (link.course?.id || '')) ||
    (editProf &&
      profDirty &&
      link.professor_submission &&
      !profDuplicate &&
      profName.trim() &&
      profName.trim() !== link.professor_submission?.name);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!linkProp) return;
    const payload = {};
    if (editCourse && courseDirty && courseId !== (link.course?.id || '')) {
      payload.course = courseId;
    }
    if (
      editProf &&
      profDirty &&
      link.professor_submission &&
      !profDuplicate &&
      profName.trim() &&
      profName.trim() !== link.professor_submission?.name
    ) {
      payload.professor_name = profName.trim();
    }
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    if (onResubmit) onResubmit(link.id, payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Edit Professor-Course Link</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Department
          </Typography>
          <Typography>{link.course?.department?.name || '—'}</Typography>
          {editCourse && (
            <FormControl size="small" sx={{ minWidth: 180, mt: 1 }}>
              <Select
                value={deptId}
                onChange={(e) => {
                  setDeptId(e.target.value);
                  setCourseDirty(true);
                }}
              >
                {departments.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Current Course Selection
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>
              {link.course ? `${link.course.short_name} – ${link.course.long_name}` : '—'}
            </Typography>
            <IconOnlyChip icon={<EditIcon fontSize="small" />} onClick={toggleEditCourse} />
          </Box>
          {editCourse && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={courseId}
                  onChange={(e) => {
                    setCourseId(e.target.value);
                    setCourseDirty(true);
                  }}
                >
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.short_name} – {c.long_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconOnlyChip icon={<CancelIcon fontSize="small" />} onClick={toggleEditCourse} />
            </Box>
          )}
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Professor
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>{link.professor?.name || link.professor_submission?.name || '—'}</Typography>
            {link.professor_submission && (
              <IconOnlyChip icon={<EditIcon fontSize="small" />} onClick={toggleEditProf} />
            )}
          </Box>
          {editProf && link.professor_submission && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <TextField
                size="small"
                value={profName}
                onChange={(e) => {
                  setProfName(e.target.value);
                  setProfDirty(true);
                }}
                placeholder="New Professor Name"
                error={profDuplicate}
                helperText={profDuplicate ? 'Professor already exists' : ''}
              />
              <IconOnlyChip icon={<CancelIcon fontSize="small" />} onClick={toggleEditProf} />
            </Box>
          )}
        </Box>
        {link.rejection_reason && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Reason for Rejection
            </Typography>
            <Typography>{link.rejection_reason}</Typography>
          </Box>
        )}
        {link.rejection_reason && link.previous_submission && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Previous Submission
            </Typography>
            <Typography>{prevDisplay}</Typography>
          </Box>
        )}
      </DialogContent>
      <FormControlLabel
        control={
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
        }
        label="I confirm that by submitting this edit I'm transferring the ownership of the submission to myself."
        sx={{ mx: 3 }}
      />
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton
          type="submit"
          disabled={!confirmed || !changed}
          text="Resubmit"
          color="info"
        />
      </DialogActions>
    </Dialog>
  );
};

export default EditProfessorCourseAdminDialog;
